/**
 * Curtain Calculator Configuration Loader
 * Loads and manages configuration data for the curtain calculator
 * Singleton pattern with Promise-based initialization
 */

class CurtainConfigLoader {
  constructor() {
    this.config = null;
    this.fabricData = null;
    this.isLoaded = false;
    this.loadingPromise = null;
    this.initializationPromise = null;
  }

  /**
   * Get the singleton initialization promise
   * This ensures all components wait for the same configuration loading process
   * @returns {Promise} Initialization promise
   */
  getInitializationPromise() {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this._initializeConfigurations();
    return this.initializationPromise;
  }

  /**
   * Private method to handle the complete initialization process
   * @private
   */
  async _initializeConfigurations() {
    try {
      // Wait for configuration injection from Liquid template
      await this._waitForConfigurationInjection();
      
      // Load configurations
      await this.loadConfigurations();
      
      return this;
    } catch (error) {
      console.error('Configuration initialization failed:', error);
      throw error;
    }
  }

  /**
   * Wait for configuration data to be injected by Liquid template
   * @private
   */
  _waitForConfigurationInjection() {
    return new Promise((resolve, reject) => {
      // Check if configurations are already available
      if (window.curtainCalculatorConfig && window.fabricDataConfig) {
        resolve();
        return;
      }

      // Set up timeout
      const timeout = setTimeout(() => {
        reject(new Error('Configuration injection timeout - configurations not loaded within 10 seconds'));
      }, 10000);

      // Listen for configuration loaded event
      const handleConfigLoaded = () => {
        clearTimeout(timeout);
        resolve();
      };

      window.addEventListener('curtainConfigsLoaded', handleConfigLoaded, { once: true });
    });
  }

  /**
   * Load configuration files (now synchronous since data is pre-injected)
   * @returns {Promise} Loading promise
   */
  async loadConfigurations() {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this._loadConfigFiles();
    return this.loadingPromise;
  }

  /**
   * Private method to load config files
   * @private
   */
  async _loadConfigFiles() {
    try {
      // Check if configurations are mounted on window object
      if (!window.curtainCalculatorConfig || !window.fabricDataConfig) {
        throw new Error('Configuration data not found on window object. Please ensure configurations are properly injected from Liquid template.');
      }

      // Load configurations from window object
      this.config = window.curtainCalculatorConfig;
      this.fabricData = window.fabricDataConfig;

      this.isLoaded = true;
      
      return {
        config: this.config,
        fabricData: this.fabricData
      };
    } catch (error) {
      console.error('Error loading curtain calculator configurations:', error);
      throw error;
    }
  }

  /**
   * Get main configuration
   * @returns {Object} Main configuration object
   */
  getConfig() {
    if (!this.isLoaded) {
      throw new Error('Configuration not loaded. Call loadConfigurations() first.');
    }
    return this.config.curtain_calculator_config;
  }

  /**
   * Get fabric data configuration
   * @returns {Object} Fabric data configuration object
   */
  getFabricData() {
    if (!this.isLoaded) {
      throw new Error('Configuration not loaded. Call loadConfigurations() first.');
    }
    return this.fabricData.fabric_data_config;
  }

  /**
   * Get header types
   * @returns {Object} Header types configuration
   */
  getHeaderTypes() {
    const config = this.getConfig();
    return config.header_types;
  }

  /**
   * Get enabled header types
   * @returns {Object} Enabled header types only
   */
  getEnabledHeaderTypes() {
    const headerTypes = this.getHeaderTypes();
    const enabled = {};
    
    Object.keys(headerTypes).forEach(key => {
      if (headerTypes[key].enabled) {
        enabled[key] = headerTypes[key];
      }
    });
    
    return enabled;
  }

  /**
   * Get header type by ID
   * @param {string} headerId - Header type ID
   * @returns {Object|null} Header type configuration or null if not found
   */
  getHeaderType(headerId) {
    const headerTypes = this.getHeaderTypes();
    return headerTypes[headerId] || null;
  }

  /**
   * Get lining options
   * @returns {Object} Lining options configuration
   */
  getLiningOptions() {
    const config = this.getConfig();
    return config.lining_options;
  }

  /**
   * Get enabled lining options
   * @returns {Object} Enabled lining options only
   */
  getEnabledLiningOptions() {
    const liningOptions = this.getLiningOptions();
    const enabled = {};
    
    Object.keys(liningOptions).forEach(key => {
      if (liningOptions[key].enabled !== false) {
        enabled[key] = liningOptions[key];
      }
    });
    
    return enabled;
  }

  /**
   * Get additional services
   * @returns {Object} Additional services configuration
   */
  getAdditionalServices() {
    const config = this.getConfig();
    return config.additional_services;
  }

  /**
   * Get base settings
   * @returns {Object} Base settings configuration
   */
  getBaseSettings() {
    const config = this.getConfig();
    return config.base_settings;
  }

  /**
   * Get calculation rules
   * @returns {Object} Calculation rules configuration
   */
  getCalculationRules() {
    const config = this.getConfig();
    return config.calculation_rules;
  }

  /**
   * Get default fabrics
   * @returns {Object} Default fabrics configuration
   */
  getDefaultFabrics() {
    const fabricData = this.getFabricData();
    return fabricData.fabrics;
  }

  /**
   * Get enabled default fabrics
   * @returns {Object} Enabled default fabrics only
   */
  getEnabledDefaultFabrics() {
    const fabrics = this.getDefaultFabrics();
    // In the new configuration, all fabrics are enabled by default
    return fabrics;
  }

  /**
   * Get fabric by ID
   * @param {string} fabricId - Fabric ID
   * @returns {Object|null} Fabric configuration or null if not found
   */
  getFabric(fabricId) {
    const fabrics = this.getDefaultFabrics();
    return fabrics[fabricId] || null;
  }

  /**
   * Check if fabric supports heat setting
   * @param {string} fabricId - Fabric ID
   * @returns {boolean} True if fabric supports heat setting
   */
  fabricSupportsHeatSetting(fabricId) {
    const fabric = this.getFabric(fabricId);
    return fabric ? fabric.supports_heat_setting : false;
  }

  /**
   * Get UI configuration
   * @returns {Object} UI configuration
   */
  getUIConfig() {
    const config = this.getConfig();
    return config.ui_config;
  }

  /**
   * Get currency symbol
   * @returns {string} Currency symbol
   */
  getCurrencySymbol() {
    const uiConfig = this.getUIConfig();
    return uiConfig.default_currency_symbol;
  }

  /**
   * Get decimal places for price display
   * @returns {number} Number of decimal places
   */
  getDecimalPlaces() {
    const uiConfig = this.getUIConfig();
    return uiConfig.decimal_places;
  }

  /**
   * Get conversion factor from inches to cm
   * @returns {number} Conversion factor
   */
  getInchToCmConversion() {
    const baseSettings = this.getBaseSettings();
    return baseSettings.conversions.inch_to_cm;
  }

  /**
   * Get conversion factor from cm to meters
   * @returns {number} Conversion factor
   */
  getCmToMetersConversion() {
    const baseSettings = this.getBaseSettings();
    return baseSettings.conversions.cm_to_meters;
  }

  /**
   * Convert inches to centimeters
   * @param {number} inches - Value in inches
   * @returns {number} Value in centimeters
   */
  inchesToCm(inches) {
    return inches * this.getInchToCmConversion();
  }

  /**
   * Convert centimeters to meters
   * @param {number} cm - Value in centimeters
   * @returns {number} Value in meters
   */
  cmToMeters(cm) {
    return cm * this.getCmToMetersConversion();
  }
}

// Create global instance
window.curtainConfigLoader = new CurtainConfigLoader();

// Export for module use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CurtainConfigLoader;
} 